import {
  DEFAULT_DEBOUNCE_DELAY,
  IPaginationMetadata,
  ORDER_TYPE,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import {
  DeleteInternshipLocationUseCaseInterface,
  GetAllInternshipLocationsUseCaseInterface,
} from 'domain/usecases';
import { INTERNSHIP_LOCATIONS_ORDER_COLUMNS } from 'domain/value-objects/internship-locations-order-columns';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearInternshipLocationsListData,
  loadInternshipLocationsListData,
} from 'presentation/config/store/internship-locations/internship-locations-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const useInternshipLocationsList = () => {
  // Hooks
  const navigate = useNavigate();
  const { internshipLocationsService } = useService();
  const dispatch = useDispatch();

  // States
  const { internshipLocationsListData } = useAppSelector(
    (state) => state.InternshipLocations,
  );
  const [paginationMetadata, setPaginationMetadata] = useState<
    IPaginationMetadata<INTERNSHIP_LOCATIONS_ORDER_COLUMNS>
  >({
    selectedPage: 0,
    rowsPerPage: 10,
    searchString: '',
    orderColumn: INTERNSHIP_LOCATIONS_ORDER_COLUMNS.NAME,
    sortDirection: ORDER_TYPE.ASC,
  });
  const [listStatus, setListStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  // Methods
  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<INTERNSHIP_LOCATIONS_ORDER_COLUMNS>>(
      key: T,
      value: IPaginationMetadata<INTERNSHIP_LOCATIONS_ORDER_COLUMNS>[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<
        GetAllInternshipLocationsUseCaseInterface.Result['data'][number]
      >,
      sortDirection: SortOrder,
    ) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as INTERNSHIP_LOCATIONS_ORDER_COLUMNS,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const requestGetAllInternshipLocations = useCallback(
    async (params: GetAllInternshipLocationsUseCaseInterface.Params) => {
      setListStatus(STATUSPAGE.LOADING);
      await internshipLocationsService
        .getAllInternshipLocations(params)
        .then((res) => {
          dispatch(loadInternshipLocationsListData(res));
        })
        .catch((res) => {
          notifyError(res);
          dispatch(clearInternshipLocationsListData());
        })
        .finally(() => {
          setListStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const debouncedRequestGetAllInternshipLocations = useMemo(
    () =>
      debounce(
        (valuesToSubmit: GetAllInternshipLocationsUseCaseInterface.Params) => {
          requestGetAllInternshipLocations(valuesToSubmit);
        },
        DEFAULT_DEBOUNCE_DELAY,
      ),

    [],
  );

  const requestDeleteInternshipLocation = useCallback(
    async (params: DeleteInternshipLocationUseCaseInterface.Params) => {
      await internshipLocationsService
        .deleteInternshipLocation(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllInternshipLocations({
            page: paginationMetadata.selectedPage.toString(),
            length: paginationMetadata.rowsPerPage.toString(),
            sortDirection: paginationMetadata.sortDirection,
            sortBy: paginationMetadata.orderColumn,
            search: encodeURIComponent(paginationMetadata.searchString),
          });
        })
        .catch((err) => {
          notifyError(
            err.message || 'Não foi possível excluir o Local de Estágio.',
          );
        });
    },
    [paginationMetadata],
  );

  return {
    listStatus,
    paginationMetadata,
    internshipLocationsListData,
    debouncedRequestGetAllInternshipLocations,
    navigate,
    setListStatus,
    handlePageChange,
    handleSortRemote,
    handleSearchRemote,
    handleRowsPerPageChange,
    requestDeleteInternshipLocation,
  };
};
